// Thin client for the backend /api/auth/* endpoints. Keeps all HTTP
// shape knowledge in one place so the rest of the app imports typed
// functions instead of stringly-typed fetch calls.

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export type ArchitectStage = 'architecture' | 'estimate' | 'pricing' | 'proposal';
export interface StageCapability {
  view: boolean;
  edit: boolean;
  approve: boolean;
  configured: boolean;
  edit_client_document?: boolean;
}
export interface AuthedUser {
  id: string;
  username: string;
  email: string;
  displayName: string | null;
  isRoot: boolean;
  isDisabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  stage_access?: Record<ArchitectStage, StageCapability>;
}

export interface PermissionDef {
  id: string;
  label: string;
  description: string;
  group: string;
}

export interface AuthResponse {
  token: string;
  user: AuthedUser;
  permissions: string[];
}

export interface BootstrapStatus {
  bootstrapped: boolean;
  userCount: number;
}

// Localized wrapper: always sends JSON, always parses JSON, never
// throws for non-2xx (caller decides what to do with the shape).
async function post<T>(path: string, body: unknown, token?: string): Promise<{ ok: boolean; status: number; data: T & { error?: string; message?: string } }> {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({} as any));
  return { ok: res.ok, status: res.status, data: data as T & { error?: string; message?: string } };
}

async function get<T>(path: string, token?: string): Promise<{ ok: boolean; status: number; data: T & { error?: string; message?: string } }> {
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({} as any));
  return { ok: res.ok, status: res.status, data: data as T & { error?: string; message?: string } };
}

export const authApi = {
  status:    () => get<BootstrapStatus>('/api/auth/status'),
  bootstrap: (body: { secret: string; username: string; email: string; password: string; displayName?: string }) =>
    post<AuthResponse>('/api/auth/bootstrap', body),
  login:     (body: { identifier: string; password: string }) =>
    post<AuthResponse>('/api/auth/login', body),
  me:        (token: string) => get<{ user: AuthedUser; permissions: string[] }>('/api/auth/me', token),
  logout:    (token: string) => post<{ ok: true }>('/api/auth/logout', {}, token),
  permissions: () => get<{ permissions: PermissionDef[] }>('/api/auth/permissions'),
};

// ─── Architect (mini-Miro) hand-off ─────────────────────────
// The Architect SPA is served by the backend at /architect. An
// account whose ONLY grant is architect:use ("miro creator") has no
// business in this admin — the backend 403s every other endpoint —
// so the Gate bounces it there right after login. The token rides in
// the URL fragment (never sent to the server); the Architect app
// stores it and strips the hash immediately.
export const ARCHITECT_URL =
  (process.env.REACT_APP_ARCHITECT_URL || `${API}/architect/`).replace(/\/+$/, '/');

export function architectUrl(token?: string | null): string {
  return token ? `${ARCHITECT_URL}#token=${encodeURIComponent(token)}` : ARCHITECT_URL;
}

export function isArchitectOnly(user: AuthedUser | null, permissions: string[]): boolean {
  return !!user
    && !user.isRoot
    && permissions.includes('architect:use')
    && permissions.every((p) => p === 'architect:use');
}

// ─── Root user administration (root-only endpoints) ─────────
// The Root panel manages accounts. These endpoints are root-gated
// server-side (403 for anyone else); the global fetchInterceptor
// attaches the Bearer token from storage, so there's no explicit
// token plumbing here. Unlike the wrappers above these RESOLVE with
// the payload directly and THROW an AuthApiError on failure — the
// error carries both the server code (for mapping user_exists → …)
// and the raw server message (for validation copy).

// A user row as returned by GET /api/auth/users: the public user plus
// its inlined effective permission ids.
export type AdminUser = AuthedUser & { permissions: string[] };

export class AuthApiError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.name = 'AuthApiError';
    this.code = code;
  }
}

// Read {error, message} off a non-2xx response and throw. Never resolves.
async function failAuth(res: Response): Promise<never> {
  const data = await res.json().catch(() => ({} as { error?: string; message?: string }));
  throw new AuthApiError(data.error || 'request_failed', data.message);
}

// GET /api/auth/users — backend returns a BARE array; tolerate a
// {users:[…]} envelope too so a shape change doesn't blank the panel.
export async function listUsers(): Promise<AdminUser[]> {
  const res = await fetch(`${API}/api/auth/users`);
  if (!res.ok) return failAuth(res);
  const data = await res.json().catch(() => [] as unknown);
  if (Array.isArray(data)) return data as AdminUser[];
  const users = (data as { users?: AdminUser[] }).users;
  return Array.isArray(users) ? users : [];
}

// POST /api/auth/users — creates an account. Non-root by default; pass
// isRoot:true to mint a second root (the backend then ignores the
// permissions array — roots hold everything implicitly). Optional
// permission grants applied server-side after create.
export async function createUser(input: {
  username: string;
  email: string;
  password: string;
  displayName?: string;
  permissions: string[];
  isRoot?: boolean;
}): Promise<{ user: AuthedUser; permissions: string[] }> {
  const res = await fetch(`${API}/api/auth/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return failAuth(res);
  return res.json();
}

// POST /api/auth/users/:id/disabled — block / unblock an account (root).
// Blocking ends every session the account holds; the server refuses to
// disable the caller themselves or the last enabled root.
export async function setUserDisabled(
  id: string,
  disabled: boolean,
): Promise<{ user: AuthedUser }> {
  const res = await fetch(`${API}/api/auth/users/${encodeURIComponent(id)}/disabled`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ disabled }),
  });
  if (!res.ok) return failAuth(res);
  return res.json();
}

// POST /api/auth/users/:id/permissions — grant and/or revoke; resolves
// with the new effective permission list for the user.
export async function setUserPermissions(
  id: string,
  body: { grant?: string[]; revoke?: string[] },
): Promise<{ permissions: string[] }> {
  const res = await fetch(`${API}/api/auth/users/${encodeURIComponent(id)}/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return failAuth(res);
  return res.json();
}
